/**
 * Checks whether a CSS variable has a value applied in the computed styles.
 *
 * @param variableExpression - A CSS var() expression.
 * @returns True if the CSS variable is defined and has a value; otherwise, false.
 */
export const isCSSVariableApplied = (variableExpression) => {
    const varRegex = /^var\(\s*(--[\w-]+)\s*,\s*([^)]+)\)$/;
    const match = variableExpression.match(varRegex);
    if (!match) {
        return false; // Not a valid var() expression
    }
    const [, variableName] = match;
    const root = document.documentElement;
    const styles = getComputedStyle(root);
    const variableValue = styles.getPropertyValue(variableName).trim();
    return variableValue.length > 0;
};
/**
 * Resolves a CSS variable value from a var() expression, supporting fallback.
 *
 * @param variableExpression - The CSS var() expression (e.g., 'var(--variable-name, fallback)').
 * @returns The resolved value as a string.
 */
export const resolveCSSVariable = (variableExpression) => {
    const varRegex = /^var\(\s*(--[\w-]+)\s*,\s*([^)]+)\)$/;
    const match = variableExpression.match(varRegex);
    if (!match) {
        // If it is not in var() format, return it as is
        return variableExpression.trim();
    }
    const [, variableName, fallbackValue] = match;
    const root = document.documentElement;
    const styles = getComputedStyle(root);
    const variableValue = styles.getPropertyValue(variableName).trim();
    return variableValue || fallbackValue.trim();
};
export const isValidHexColor = (color) => {
    const hexRegex = /^#[0-9a-fA-F]{6}$/;
    return hexRegex.test(color);
};
export const isValidCssColor = (color) => {
    const s = new Option().style;
    s.color = color;
    return s.color !== '';
};
export function hexToRgbA(hex, alpha) {
    const resolvedHex = resolveCSSVariable(hex);
    if (isValidHexColor(resolvedHex)) {
        if (alpha < 0 || alpha > 1) {
            throw new Error('Invalid alpha value');
        }
        const r = parseInt(resolvedHex.slice(1, 3), 16);
        const g = parseInt(resolvedHex.slice(3, 5), 16);
        const b = parseInt(resolvedHex.slice(5, 7), 16);
        return alpha
            ? `rgba(${r}, ${g}, ${b}, ${alpha})`
            : `rgb(${r}, ${g}, ${b})`;
    }
    if (isValidCssColor(resolvedHex)) {
        const tempElement = document.createElement('div');
        tempElement.style.color = resolvedHex; // corrected from "colorName" to "resolvedHex"
        document.body.appendChild(tempElement);
        const computedColor = getComputedStyle(tempElement).color;
        document.body.removeChild(tempElement);
        const rgbMatch = computedColor.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/);
        if (!rgbMatch) {
            return ''; // Return empty string for invalid color format
        }
        const [, r, g, b] = rgbMatch.map(Number);
        if (alpha < 0 || alpha > 1) {
            throw new Error('Invalid alpha value');
        }
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
    return '';
}
//# sourceMappingURL=color-utils.js.map